import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X, Funnel } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../../../lib/utils";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

export const ALLOCATION_TYPE_OPTIONS = [
  { value: "Sales", labelKey: "inventory.sales" },
  { value: "Service", labelKey: "inventory.service" },
];

export const EMPTY_CUSTOMER_ALLOCATION_FILTERS = {
  customerId: "All",
  allocationType: "All",
};

export function CustomerAllocationFiltersModal({
  open,
  onOpenChange,
  appliedFilters,
  onApply,
  customerOptions = [],
  loadingCustomers = false,
  loadingMoreCustomers = false,
  onCustomerListScroll,
}) {
  const { t } = useTranslation();
  const [tempFilters, setTempFilters] = useState({
    ...EMPTY_CUSTOMER_ALLOCATION_FILTERS,
  });

  useEffect(() => {
    if (open) {
      setTempFilters({
        customerId: appliedFilters.customerId || "All",
        allocationType: appliedFilters.allocationType || "All",
      });
    }
  }, [open, appliedFilters]);

  const handleChange = (key, value) => {
    setTempFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApply = () => {
    onApply({ ...tempFilters });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/40 backdrop-blur-xs fixed inset-0 z-40 transition-opacity animate-in fade-in duration-200" />

        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] sm:w-[90vw] md:w-[450px]",
            "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
            "p-6 flex flex-col gap-5 font-sans focus:outline-hidden",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          )}
        >
          <div className="flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <Funnel className="h-4.5 w-4.5 text-primary" />
              <DialogTitle className="text-[16px] font-bold text-foreground">
                {t("formActions.filterCustomerAllocations")}
              </DialogTitle>
            </div>
            <DialogPrimitive.Close className="h-8 w-8 grid place-items-center rounded-lg per-file-hint hover:bg-workspace/60 hover:text-primary transition focus:outline-hidden">
              <X className="h-4 w-4" />
              <span className="sr-only">{t("formActions.close")}</span>
            </DialogPrimitive.Close>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-foreground">
                {t("inventory.customerName")}
              </label>
              <Select
                value={tempFilters.customerId}
                onValueChange={(val) => handleChange("customerId", val)}
              >
                <SelectTrigger className="h-10 rounded-xl border border-(--color-text-border) bg-white text-xs font-semibold focus:ring-accent w-full">
                  <SelectValue placeholder={t("inventory.allCustomers")} />
                </SelectTrigger>
                <SelectContent
                  className="z-[100]"
                  viewportClassName="h-auto max-h-60 overflow-y-auto"
                  onViewportScroll={onCustomerListScroll}
                  loading={loadingCustomers}
                >
                  <SelectItem value="All">
                    {t("inventory.allCustomers")}
                  </SelectItem>
                  {customerOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                  {loadingCustomers || loadingMoreCustomers ? (
                    <div className="px-2 py-1.5 text-xs per-file-hint text-center">
                      {t("common.loading")}
                    </div>
                  ) : null}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-foreground">
                {t("inventory.salesAndService")}
              </label>
              <Select
                value={tempFilters.allocationType}
                onValueChange={(val) => handleChange("allocationType", val)}
              >
                <SelectTrigger className="h-10 rounded-xl border border-(--color-text-border) bg-white text-xs font-semibold focus:ring-accent w-full">
                  <SelectValue
                    placeholder={t("inventory.allAllocationTypes")}
                  />
                </SelectTrigger>
                <SelectContent className="z-[100]">
                  <SelectItem value="All">
                    {t("inventory.allAllocationTypes")}
                  </SelectItem>
                  {ALLOCATION_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-3 border-t border-(--color-text-border)/50 shrink-0">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="h-10 px-4 rounded-xl border border-(--color-text-border) per-file-hint font-bold text-xs hover:bg-gray-50 transition cursor-pointer"
            >
              {t("formActions.cancel")}
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="h-10 px-5 rounded-xl bg-primary text-white font-bold text-xs hover:bg-primary/90 transition cursor-pointer shadow-sm"
            >
              {t("formActions.applyFilters")}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
